import { useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { fetchCalendar, fetchUnits, CalendarEntry, Unit } from "@/api/calendar";
import { Card } from "@/components/Card";
import { PageHeader } from "@/components/PageHeader";
import { Btn } from "@/components/Btn";
import { useDocumentTitle } from "@/lib/useDocumentTitle";

const MONTHS_DE = [
  "Januar","Februar","März","April","Mai","Juni",
  "Juli","August","September","Oktober","November","Dezember",
];

const DOW_SHORT = ["S","M","D","M","D","F","S"];

const UNIT_COLOR_MAP: Record<string, string> = {
  "EG": "var(--unit-eg)",
  "OG": "var(--unit-og)",
  "Zeltplatz": "var(--unit-zp)",
  "DG": "var(--unit-dg)",
  "LZ": "var(--unit-lz)",
};

function unitColor(name: string): string {
  return UNIT_COLOR_MAP[name] ?? "var(--brand-700)";
}

const STATUS_COLOR_MAP: Record<string, string> = {
  "Angelegt":                 "var(--status-new)",
  "Bestätigt":                "var(--status-confirm)",
  "Konus Link versandt":      "var(--status-konus-link)",
  "Konus-Daten eingegeben":   "var(--status-konus)",
  "Infos an Gruppe versandt": "var(--status-info)",
  "Abgereist":                "var(--status-done)",
  "Rechnung erstellt":        "var(--status-bill)",
  "Bezahlt":                  "var(--status-closed)",
  "Intern":                   "var(--status-intern)",
};

const STATUS_ORDER = Object.keys(STATUS_COLOR_MAP);

function statusColor(status: string): string {
  return STATUS_COLOR_MAP[status] ?? "#999";
}

function daysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

function findConflictIds(bookings: CalendarEntry[]): Set<number> {
  const ids = new Set<number>();
  for (let i = 0; i < bookings.length; i++) {
    for (let j = i + 1; j < bookings.length; j++) {
      const a = bookings[i], b = bookings[j];
      if (a.check_in < b.check_out && b.check_in < a.check_out) {
        ids.add(a.id);
        ids.add(b.id);
      }
    }
  }
  return ids;
}

function isoDate(year: number, month: number, day: number): string {
  return `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

// Wandelt "HH:MM" in einen Tagesanteil 0..1 um. Ungültige/fehlende Werte
// liefern den übergebenen Default (Check-in 15:00, Check-out 10:00).
function timeFraction(time: string | null | undefined, fallback: number): number {
  if (!time) return fallback;
  const m = /^(\d{1,2}):(\d{2})/.exec(time);
  if (!m) return fallback;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (Number.isNaN(h) || Number.isNaN(min)) return fallback;
  const frac = (h * 60 + min) / (24 * 60);
  return Math.min(1, Math.max(0, frac));
}

const DEFAULT_CHECK_IN_FRACTION = 15 / 24;  // 15:00
const DEFAULT_CHECK_OUT_FRACTION = 10 / 24; // 10:00

type View = "Monat" | "Jahr";

export function CalendarPage() {
  useDocumentTitle("Kalender");
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const today = new Date();
  // Die URL ist Single Source of Truth für Monat/Jahr/Ansicht. Dadurch zeigt die
  // Browserzeile immer den echten Stand, jeder Wechsel erzeugt einen History-
  // Eintrag (Browser-Zurück funktioniert), und Zurück aus der Detailseite landet
  // wieder auf derselben Kalenderseite.
  const monthParam = searchParams.get("month");
  const yearParam = searchParams.get("year");
  const urlMonth = monthParam !== null ? Number(monthParam) : NaN;
  const urlYear = yearParam !== null ? Number(yearParam) : NaN;
  const month =
    Number.isInteger(urlMonth) && urlMonth >= 0 && urlMonth <= 11 ? urlMonth : today.getMonth();
  const year = urlYear > 0 ? urlYear : today.getFullYear();
  const view: View = searchParams.get("view") === "Jahr" ? "Jahr" : "Monat";

  // Schreibt den Kalenderzustand in die URL (Push = neuer History-Eintrag).
  const setCalState = (next: { month?: number; year?: number; view?: View }) => {
    setSearchParams({
      month: String(next.month ?? month),
      year: String(next.year ?? year),
      view: next.view ?? view,
    });
  };

  const numDays = daysInMonth(year, month);
  const monthStart = isoDate(year, month, 1);
  const monthEnd = isoDate(year, month, numDays);

  // Für Jahresansicht: ganzes Jahr laden
  const yearStart = `${year}-01-01`;
  const yearEnd = `${year}-12-31`;

  const queryVon = view === "Monat" ? monthStart : yearStart;
  const queryBis = view === "Monat" ? monthEnd : yearEnd;

  const { data: entries = [], isLoading: loadingCal } = useQuery({
    queryKey: ["calendar", queryVon, queryBis],
    queryFn: () => fetchCalendar(queryVon, queryBis),
    retry: false,
    staleTime: 0,
  });

  const { data: units = [] } = useQuery({
    queryKey: ["units"],
    queryFn: fetchUnits,
    staleTime: 5 * 60 * 1000,
  });

  const prev = () => {
    if (view === "Jahr") { setCalState({ year: year - 1 }); return; }
    if (month === 0) setCalState({ month: 11, year: year - 1 });
    else setCalState({ month: month - 1 });
  };
  const next = () => {
    if (view === "Jahr") { setCalState({ year: year + 1 }); return; }
    if (month === 11) setCalState({ month: 0, year: year + 1 });
    else setCalState({ month: month + 1 });
  };
  const goToday = () => setCalState({ month: today.getMonth(), year: today.getFullYear() });

  const bookingsInMonth = useMemo(() => entries.filter(b => {
    const s = new Date(b.check_in), e = new Date(b.check_out);
    const ms = new Date(year, month, 1);
    const me = new Date(year, month, numDays);
    return e >= ms && s <= me;
  }), [entries, year, month, numDays]);

  const subtitle = view === "Monat"
    ? `${MONTHS_DE[month]} ${year} · ${bookingsInMonth.length} Buchungen`
    : `${year}`;

  return (
    <div>
      <PageHeader
        title="Belegungskalender"
        subtitle={subtitle}
        actions={
          <>
            {/* Monat/Jahr Toggle */}
            <div style={{
              display: "inline-flex",
              background: "var(--bg-card)",
              border: "1px solid var(--border)",
              borderRadius: 8,
              padding: 2,
            }}>
              {(["Monat", "Jahr"] as View[]).map(v => (
                <button key={v} onClick={() => setCalState({ view: v })} style={{
                  padding: "5px 12px",
                  border: "none",
                  borderRadius: 6,
                  background: view === v ? "var(--brand-700)" : "transparent",
                  color: view === v ? "#fff" : "var(--fg-muted)",
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: "pointer",
                  fontFamily: "var(--font-body)",
                  transition: "background .12s, color .12s",
                }}>{v}</button>
              ))}
            </div>

            {/* Navigation */}
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <button onClick={prev} style={navBtnStyle()}>
                <span className="material-icons-outlined" style={{ fontSize: 16 }}>chevron_left</span>
              </button>
              <button onClick={goToday} style={{
                padding: "6px 10px",
                background: "var(--bg-card)",
                border: "1px solid var(--border)",
                borderRadius: 6,
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
                color: "var(--fg)",
                fontFamily: "var(--font-body)",
              }}>Heute</button>
              <button onClick={next} style={navBtnStyle()}>
                <span className="material-icons-outlined" style={{ fontSize: 16 }}>chevron_right</span>
              </button>
            </div>

            <Btn variant="primary" icon="add" onClick={() => navigate("/bookings/new")}>
              Neue Buchung
            </Btn>
          </>
        }
      />

      {loadingCal && (
        <div style={{ padding: 24, fontSize: 13, color: "var(--fg-muted)" }}>Lade Kalender…</div>
      )}

      {!loadingCal && view === "Monat" && (
        <MonthView
          year={year}
          month={month}
          numDays={numDays}
          entries={entries}
          units={units}
          today={today}
          onBookingClick={(id) => navigate(`/bookings/${id}`, { state: { from: `/calendar?month=${month}&year=${year}&view=${view}` } })}
        />
      )}

      {!loadingCal && view === "Jahr" && (
        <YearView
          year={year}
          entries={entries}
          onMonthClick={(m) => setCalState({ month: m, view: "Monat" })}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Monatsansicht (Gantt)
// ---------------------------------------------------------------------------

function MonthView({
  year, month, numDays, entries, units, today, onBookingClick,
}: {
  year: number;
  month: number;
  numDays: number;
  entries: CalendarEntry[];
  units: Unit[];
  today: Date;
  onBookingClick: (id: number) => void;
}) {
  const days = Array.from({ length: numDays }, (_, i) => i + 1);
  const todayInView = today.getFullYear() === year && today.getMonth() === month;

  // Einheiten sortiert (API-Reihenfolge), fehlende Einheiten aus Buchungen ergänzen
  const unitNames = useMemo(() => {
    const fromApi = units.map(u => u.name);
    const fromBookings = entries.map(e => e.einheit_name).filter(Boolean) as string[];
    const all = [...new Set([...fromApi, ...fromBookings])];
    return all.length > 0 ? all : ["Unbekannt"];
  }, [units, entries]);

  const conflictIdsByUnit = useMemo(() => {
    const result: Record<string, Set<number>> = {};
    for (const unitName of unitNames) {
      const ub = entries.filter(b => b.einheit_name === unitName);
      result[unitName] = findConflictIds(ub);
    }
    return result;
  }, [entries, unitNames]);

  return (
    <Card padded={false} style={{ overflow: "hidden" }}>
      {/* Tag-Header */}
      <div style={{ display: "flex", borderBottom: "1px solid var(--border)", background: "var(--bg-subtle)" }}>
        <div style={{
          width: 140, flexShrink: 0, padding: "10px 14px",
          fontSize: 11, textTransform: "uppercase", letterSpacing: ".06em",
          color: "var(--fg-muted)", fontWeight: 600,
          borderRight: "1px solid var(--border)",
        }}>
          Einheit
        </div>
        <div style={{ flex: 1, display: "flex", overflow: "hidden" }}>
          {days.map(d => {
            const dow = new Date(year, month, d).getDay();
            const isWe = dow === 0 || dow === 6;
            const isToday = todayInView && d === today.getDate();
            return (
              <div key={d} style={{
                flex: 1,
                minWidth: 26,
                textAlign: "center",
                padding: "6px 0",
                fontSize: 10,
                fontWeight: isToday ? 700 : 500,
                color: isToday ? "#fff" : (isWe ? "var(--accent)" : "var(--fg-muted)"),
                background: isToday
                  ? "var(--brand-700)"
                  : (isWe ? "color-mix(in oklab, var(--accent) 6%, transparent)" : "transparent"),
                borderRight: "1px solid var(--border)",
                lineHeight: 1.2,
              }}>
                <div style={{ textTransform: "uppercase", fontSize: 9, opacity: .7 }}>{DOW_SHORT[dow]}</div>
                <div style={{ fontSize: 12, marginTop: 1 }}>{d}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Einheiten-Zeilen */}
      {unitNames.map(unitName => {
        const color = unitColor(unitName);
        const unitBookings = entries.filter(b => b.einheit_name === unitName);
        const conflictIds = conflictIdsByUnit[unitName] ?? new Set<number>();
        const hasConflict = conflictIds.size > 0;

        return (
          <div key={unitName} style={{
            display: "flex",
            borderBottom: "1px solid var(--border)",
            minHeight: 76,
            position: "relative",
          }}>
            {/* Einheit-Label */}
            <div style={{
              width: 140,
              flexShrink: 0,
              padding: 14,
              borderRight: "1px solid var(--border)",
              display: "flex",
              flexDirection: "column",
              gap: 4,
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ width: 10, height: 10, borderRadius: 3, background: color, flexShrink: 0 }} />
                <span style={{ fontSize: 13, fontWeight: 600 }}>{unitName}</span>
                {hasConflict && (
                  <span
                    className="material-icons-outlined"
                    title="Belegungskonflikt"
                    style={{ fontSize: 14, color: "#ef4444" }}
                  >
                    warning
                  </span>
                )}
              </div>
              <span style={{ fontSize: 10, color: "var(--fg-subtle)", textTransform: "uppercase", letterSpacing: ".06em" }}>
                {unitBookings.length} Buchungen
              </span>
            </div>

            {/* Grid + Balken */}
            <div style={{ flex: 1, position: "relative", overflow: "hidden" }}>
              {/* Wochend-Hintergrund */}
              <div style={{ position: "absolute", inset: 0, display: "flex" }}>
                {days.map(d => {
                  const dow = new Date(year, month, d).getDay();
                  const isWe = dow === 0 || dow === 6;
                  return (
                    <div key={d} style={{
                      flex: 1,
                      borderRight: "1px solid var(--border)",
                      background: isWe ? "color-mix(in oklab, var(--accent) 4%, transparent)" : "transparent",
                    }} />
                  );
                })}
              </div>

              {/* Heute-Linie */}
              {todayInView && (
                <div style={{
                  position: "absolute",
                  top: 0,
                  bottom: 0,
                  left: `${((today.getDate() - 0.5) / numDays) * 100}%`,
                  width: 2,
                  background: "var(--brand-700)",
                  zIndex: 2,
                  pointerEvents: "none",
                }} />
              )}

              {/* Buchungs-Balken */}
              {unitBookings.map((b) => {
                const s = new Date(b.check_in);
                const e = new Date(b.check_out);
                const monthStart = new Date(year, month, 1);
                const monthEnd = new Date(year, month, numDays);

                const startInMonth = s.getFullYear() === year && s.getMonth() === month;
                const endInMonth = e.getFullYear() === year && e.getMonth() === month;

                const startDay = s < monthStart ? 1 : (startInMonth ? s.getDate() : 1);
                const endDay = e > monthEnd ? numDays : (endInMonth ? e.getDate() : numDays);

                // Tagesanteil: liegt der echte Check-in/-out im Monat, beginnt/endet
                // der Balken anteilig (z. B. ab 15:00, bis 10:00). Ist die Buchung
                // am Monatsrand abgeschnitten, läuft sie bis zum Tagesrand.
                const startOffset = startInMonth && s >= monthStart
                  ? timeFraction(b.check_in_time, DEFAULT_CHECK_IN_FRACTION)
                  : 0;
                const endOffset = endInMonth && e <= monthEnd
                  ? timeFraction(b.check_out_time, DEFAULT_CHECK_OUT_FRACTION)
                  : 1;

                const startPos = (startDay - 1) + startOffset;
                const endPos = (endDay - 1) + endOffset;

                const leftPct = (startPos / numDays) * 100;
                const widthPct = (Math.max(endPos - startPos, 0) / numDays) * 100;
                const barColor = statusColor(b.status);
                const isConflict = conflictIds.has(b.id);

                return (
                  <div
                    key={b.id}
                    onClick={() => onBookingClick(b.id)}
                    title={isConflict ? `⚠ Konflikt: ${b.gastname} · ${b.status}` : `${b.gastname} · ${b.status}`}
                    style={{
                      position: "absolute",
                      left: `calc(${leftPct}% + 3px)`,
                      width: `calc(${widthPct}% - 6px)`,
                      top: 10,
                      height: 56,
                      background: barColor,
                      borderRadius: 6,
                      padding: "6px 10px",
                      color: "#fff",
                      overflow: "hidden",
                      cursor: "pointer",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                      fontSize: 11,
                      fontWeight: 600,
                      lineHeight: 1.2,
                      zIndex: 1,
                      boxShadow: isConflict
                        ? "0 0 0 2px #ef4444, inset 0 -2px 0 rgba(0,0,0,.12)"
                        : "inset 0 -2px 0 rgba(0,0,0,.12)",
                      transition: "opacity .12s",
                    }}
                    onMouseEnter={ev => (ev.currentTarget.style.opacity = ".85")}
                    onMouseLeave={ev => (ev.currentTarget.style.opacity = "1")}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                      {isConflict && (
                        <span className="material-icons-outlined" style={{ fontSize: 11, flexShrink: 0 }}>warning</span>
                      )}
                      <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {b.gastname}
                      </span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 10, fontWeight: 500, opacity: .9 }}>
                      <span className="material-icons-outlined" style={{ fontSize: 11 }}>groups</span>
                      <span className="mono">{b.buchungsreferenz}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {/* Legende */}
      <div style={{
        padding: "10px 14px",
        display: "flex",
        gap: 16,
        fontSize: 11,
        color: "var(--fg-muted)",
        background: "var(--bg-subtle)",
        borderTop: "1px solid var(--border)",
        alignItems: "center",
        flexWrap: "wrap",
      }}>
        <span style={{ fontWeight: 600, color: "var(--fg)" }}>Legende:</span>
        {STATUS_ORDER.map((s) => (
          <span key={s} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <span style={{ width: 14, height: 10, background: STATUS_COLOR_MAP[s], borderRadius: 3 }} />
            {s}
          </span>
        ))}
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <span style={{ width: 2, height: 14, background: "var(--brand-700)" }} />
          Heute
        </span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <span className="material-icons-outlined" style={{ fontSize: 13, color: "#ef4444" }}>warning</span>
          Konflikt
        </span>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Jahresansicht
// ---------------------------------------------------------------------------

function YearView({
  year, entries, onMonthClick,
}: {
  year: number;
  entries: CalendarEntry[];
  onMonthClick: (month: number) => void;
}) {
  return (
    <Card>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 14 }}>
        {MONTHS_DE.map((mName, i) => {
          const daysInM = daysInMonth(year, i);
          const mBookings = entries.filter(b => {
            const s = new Date(b.check_in);
            return s.getFullYear() === year && s.getMonth() === i;
          });

          return (
            <MonthCard
              key={mName}
              monthName={mName}
              year={year}
              monthIndex={i}
              daysInM={daysInM}
              bookingCount={mBookings.length}
              entries={entries}
              onClick={() => onMonthClick(i)}
            />
          );
        })}
      </div>
    </Card>
  );
}

function MonthCard({
  monthName, year, monthIndex, daysInM, bookingCount, entries, onClick,
}: {
  monthName: string;
  year: number;
  monthIndex: number;
  daysInM: number;
  bookingCount: number;
  entries: CalendarEntry[];
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        textAlign: "left",
        border: "1px solid var(--border)",
        borderRadius: 10,
        padding: 12,
        background: "var(--bg-card)",
        cursor: "pointer",
        fontFamily: "var(--font-body)",
        transition: "background .12s, border-color .12s",
        width: "100%",
      }}
      onMouseEnter={e => {
        e.currentTarget.style.borderColor = "var(--brand-700)";
        e.currentTarget.style.background = "var(--bg-hover)";
      }}
      onMouseLeave={e => {
        e.currentTarget.style.borderColor = "var(--border)";
        e.currentTarget.style.background = "var(--bg-card)";
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 8 }}>
        <div className="display" style={{ fontSize: 16, color: "var(--brand-900)" }}>{monthName}</div>
        <div style={{ fontSize: 11, color: "var(--fg-muted)" }}>{bookingCount} Buch.</div>
      </div>
      {/* Mini-Auslastungsbalken */}
      <div style={{ display: "flex", gap: 1, height: 6, borderRadius: 3, overflow: "hidden", background: "var(--bg-subtle)" }}>
        {Array.from({ length: daysInM }).map((_, d) => {
          const date = new Date(year, monthIndex, d + 1);
          const occ = entries.filter(b => {
            const s = new Date(b.check_in), e = new Date(b.check_out);
            return date >= s && date < e;
          });
          const n = occ.length;
          return (
            <div
              key={d}
              style={{
                flex: 1,
                background: n > 0
                  ? `color-mix(in oklab, var(--brand-700) ${Math.min(30 + n * 25, 100)}%, transparent)`
                  : "transparent",
              }}
            />
          );
        })}
      </div>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Hilfsfunktionen
// ---------------------------------------------------------------------------

function navBtnStyle() {
  return {
    width: 30,
    height: 30,
    borderRadius: 6,
    background: "var(--bg-card)",
    border: "1px solid var(--border)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    color: "var(--fg)",
  };
}
